import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { BRAND } from "@/lib/brand";
import { Quote } from "lucide-react";

export const ChefTestimonial = () => {
    const { t } = useTranslation();
    return (
        <section
            id="sobre-a-chef"
            data-testid="chef-section"
            className="py-24 md:py-32 bg-stone-900 text-stone-50 relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-grain opacity-30" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-stone-700 bg-stone-800">
                        <img
                            src={BRAND.chefPhoto}
                            alt="Synara Alvarenga, Personal Chef"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="chef-photo"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent" />

                        {/* Floating mini-card */}
                        <div className="absolute left-6 bottom-6 right-6 sm:right-auto sm:max-w-[260px] bg-white/95 backdrop-blur rounded-2xl px-5 py-4 shadow-xl">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="inline-block w-10 h-px bg-gold-deep" />
                                <span className="text-[10px] uppercase tracking-[0.22em] text-stone-600">
                                    {t("chef.badge_text")}
                                </span>
                            </div>
                            <p className="font-serif text-stone-900 text-lg leading-tight">
                                {t("chef.badge_phrase")}
                            </p>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-block w-14 h-px bg-gold" />
                        <span className="text-xs uppercase tracking-[0.24em] text-stone-400">
                            {t("chef.eyebrow")}
                        </span>
                    </div>

                    <Quote className="text-gold mb-6" size={40} strokeWidth={1} />

                    <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-stone-50">
                        {t("chef.quote_1")}
                        <em className="italic text-gold-soft font-normal"> {t("chef.quote_em")} </em>
                        {t("chef.quote_2")}
                    </blockquote>

                    <div className="mt-10 flex items-center gap-4">
                        <img
                            src={BRAND.logo}
                            alt="Synara Alvarenga"
                            className="h-20 w-20 object-contain"
                        />
                        <div>
                            <div className="font-script text-2xl text-gold-soft leading-none">
                                Synara Alvarenga
                            </div>
                            <div className="text-xs uppercase tracking-[0.22em] text-stone-400 mt-1">
                                {t("chef.role")}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
